import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Delete, ShieldAlert, LockKeyhole, CornerDownLeft } from "lucide-react";
import { getDeviceId } from "../lib/api";
import { ForceLogo } from "./ForceLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PIN_OK_KEY = "force_pin_ok";
const PIN_TTL_MS = 24 * 60 * 60 * 1000; // pass lasts 24h on this device

// Has this device already cleared the PIN within the last 24h?
export function pinAlreadyPassed() {
    const ts = Number(localStorage.getItem(PIN_OK_KEY) || 0);
    return ts > 0 && Date.now() - ts < PIN_TTL_MS;
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "del", "0", "enter"];

export default function PinGate({ onUnlock }) {
    const [pin, setPin] = useState("");
    const [pinLen, setPinLen] = useState(6); // configured PIN length (admin can set 4-8)
    const [status, setStatus] = useState("idle"); // idle | checking | error | locked | ok
    const [message, setMessage] = useState("");
    const [shake, setShake] = useState(0);
    // Tracks whether the gate is still on screen — used by the unlock fallback below.
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    // Show the locked screen immediately if this IP is already banned, and read
    // the configured PIN length so auto-submit fires at the right digit count.
    useEffect(() => {
        fetch(`${API}/security/state`).then((r) => r.json()).then((data) => {
            if (data?.pin_len) setPinLen(data.pin_len);
            if (data?.locked) {
                setStatus("locked");
                setMessage("This device's network has been locked for 24 hours after too many failed attempts.");
            }
        }).catch(() => {});
    }, []);

    const submit = useCallback(async (code) => {
        setStatus("checking");
        let res, data;
        try {
            res = await fetch(`${API}/security/verify-pin`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-Device-Id": getDeviceId() },
                body: JSON.stringify({ pin: code, device_id: getDeviceId() }),
            });
            data = await res.json().catch(() => ({}));
        } catch (e) {
            setStatus("error");
            setMessage("Network error. Please try again.");
            setPin("");
            setShake((s) => s + 1);
            return;
        }
        if (res.ok) {
            localStorage.setItem(PIN_OK_KEY, String(Date.now()));
            setStatus("ok");
            setMessage("Access granted");
            setTimeout(() => onUnlock && onUnlock(), 450);
            // Hard fallback: if the gate is somehow still on screen 2s after a
            // successful verify (PWA/SW state quirks), reload — the stored pass
            // lets the app boot straight in. If onUnlock worked, this component
            // has unmounted and the reload is skipped.
            setTimeout(() => {
                if (mountedRef.current) window.location.reload();
            }, 2000);
            return;
        }
        const detail = data?.detail;
        if (res.status === 429 || (detail && detail.error === "locked")) {
            setStatus("locked");
            setMessage("Too many failed attempts. This network is locked out for 24 hours.");
            setPin("");
            setShake((s) => s + 1);
            return;
        }
        const remaining = detail && typeof detail === "object" ? detail.remaining : undefined;
        setStatus("error");
        setMessage(remaining != null
            ? `Incorrect PIN. ${remaining} attempt${remaining === 1 ? "" : "s"} left before lockout.`
            : "Incorrect PIN.");
        setPin("");
        setShake((s) => s + 1);
    }, [onUnlock]);

    const press = useCallback((k) => {
        // Locked users can still type: the master PIN bypasses the lockout.
        if (status === "checking" || status === "ok") return;
        if (k === "del") { setPin((p) => p.slice(0, -1)); setStatus((s) => (s === "error" ? "idle" : s)); return; }
        if (k === "enter") { setPin((p) => { if (p.length >= 4) submit(p); return p; }); return; }
        if (!/^[0-9]$/.test(k)) return;
        setStatus((s) => (s === "error" ? "idle" : s));
        setPin((p) => (p.length >= 8 ? p : p + k));
    }, [status, submit]);

    // Auto-verify once the PIN reaches the configured length (a failed attempt
    // resets it to "", so it can re-fire cleanly). Debounced so codes longer
    // than the configured length (e.g. the master PIN) can still be typed.
    useEffect(() => {
        if (pin.length !== pinLen) return;
        const t = setTimeout(() => submit(pin), 650);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pin, pinLen]);

    // Physical keyboard support
    useEffect(() => {
        const onKey = (e) => {
            if (/^[0-9]$/.test(e.key)) press(e.key);
            else if (e.key === "Backspace") press("del");
            else if (e.key === "Enter") press("enter");
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [press]);

    const locked = status === "locked";
    const dots = Array.from({ length: Math.max(pinLen, pin.length) });

    return (
        <div className="force-dark-screen relative min-h-[100dvh] w-full overflow-hidden bg-[#050507] text-white flex items-center justify-center px-5" data-testid="pin-gate">
            {/* high-tech backdrop */}
            <div className="pointer-events-none absolute inset-0 opacity-[0.6]" style={{
                backgroundImage: "linear-gradient(rgba(255,122,0,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,122,0,0.06) 1px, transparent 1px)",
                backgroundSize: "44px 44px",
                maskImage: "radial-gradient(120% 90% at 50% 20%, #000 30%, transparent 75%)",
            }} />
            <div className="pointer-events-none absolute left-1/2 top-[-12%] h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-orange-600/20 blur-[120px]" />
            <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
                backgroundImage: "repeating-linear-gradient(0deg, #fff 0, #fff 1px, transparent 1px, transparent 3px)",
            }} />

            <motion.div
                key={shake}
                initial={{ x: 0 }}
                animate={status === "error" ? { x: [0, -12, 12, -8, 8, 0] } : { x: 0 }}
                transition={{ duration: 0.45 }}
                className="relative z-10 w-full max-w-[360px]"
            >
                <div className="mb-6 flex flex-col items-center text-center">
                    <ForceLogo size={40} />
                    <div className={`mt-6 grid h-14 w-14 place-items-center rounded-2xl border ${locked ? "border-red-500/40 bg-red-500/10" : "border-orange-500/40 bg-orange-500/10"}`}>
                        {locked ? <ShieldAlert className="h-6 w-6 text-red-400" /> : <LockKeyhole className="h-6 w-6 text-orange-400" />}
                    </div>
                    <h1 className="mt-4 font-display text-2xl font-black uppercase tracking-tight">{locked ? "Locked out" : "Secure access"}</h1>
                    <p className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">{locked ? "Access denied" : "Enter access PIN to continue"}</p>
                </div>

                {locked && (
                    <div data-testid="pin-locked" className="mb-5 rounded-2xl border border-red-500/30 bg-red-950/30 p-4 text-center">
                        <p className="text-sm text-red-200 leading-relaxed">{message}</p>
                        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-red-400/70">Master PIN holders may still enter below</p>
                    </div>
                )}
                <>
                        {/* PIN display */}
                        <div className="mb-5 flex justify-center gap-2.5" data-testid="pin-display">
                            {dots.map((_, i) => {
                                const filled = i < pin.length;
                                return (
                                    <span key={i} className={`h-3.5 w-3.5 rounded-full border transition-[background-color,border-color,transform] duration-150 ${filled ? "scale-110 border-orange-400 bg-orange-400 shadow-[0_0_12px_rgba(255,122,0,0.7)]" : "border-white/20 bg-transparent"}`} />
                                );
                            })}
                        </div>

                        {/* status message */}
                        <div className="mb-4 h-5 text-center">
                            <AnimatePresence mode="wait">
                                {(status === "error" || status === "ok") && (
                                    <motion.p
                                        key={message}
                                        data-testid="pin-message"
                                        initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                                        className={`text-xs font-semibold ${status === "ok" ? "text-emerald-400" : "text-red-400"}`}
                                    >
                                        {message}
                                    </motion.p>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* keypad */}
                        <div className="grid grid-cols-3 gap-3">
                            {KEYS.map((k) => {
                                if (k === "del") return (
                                    <button key={k} data-testid="pin-key-del" onClick={() => press("del")} className="grid h-16 place-items-center rounded-2xl border border-white/10 bg-white/[0.03] text-zinc-300 transition-[background-color,transform] hover:bg-white/10 active:scale-95">
                                        <Delete className="h-5 w-5" />
                                    </button>
                                );
                                if (k === "enter") return (
                                    <button key={k} data-testid="pin-key-enter" onClick={() => press("enter")} disabled={status === "checking"} className="grid h-16 place-items-center rounded-2xl border border-orange-500/40 bg-orange-500/15 text-orange-300 transition-[background-color,transform] hover:bg-orange-500/25 active:scale-95 disabled:opacity-50">
                                        <CornerDownLeft className="h-5 w-5" />
                                    </button>
                                );
                                return (
                                    <button
                                        key={k}
                                        data-testid={`pin-key-${k}`}
                                        onClick={() => press(k)}
                                        className="h-16 rounded-2xl border border-orange-500/25 bg-white/[0.06] font-display text-3xl font-black text-orange-400 transition-[background-color,transform,border-color] hover:border-orange-500/60 hover:bg-orange-500/15 active:scale-95"
                                    >
                                        {k}
                                    </button>
                                );
                            })}
                        </div>
                    </>
            </motion.div>

            <p className="absolute bottom-5 left-0 right-0 z-10 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">FORCE · Protected area</p>
        </div>
    );
}
